// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xHeaderState } from "@bg/App/Store/Reducers";
import { checkoutPages } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { color, xClassNames } from "@bg/XDL/Utilities";
import xScreen from "@bg/XDL/Utilities/xScreen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, Suspense, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const XngMiniCart = React.lazy(
    () => import(`@${BLUEPRINT_NAME === "bp" ? "bg" : BLUEPRINT_NAME}/App/Components/XngMiniCart/XngMiniCart`),
);

const enum fields {}

const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    const cart = getCurrentCartState(state).value;
    const billToId = getSession(state).billToId;
    const currencyCode = getBillToState(state, billToId).value?.properties["currencyCode"];
    const pageType = getCurrentPage(state).type;
    const shouldLoadCart =
        !getCurrentCartState(state).value &&
        !getCurrentCartState(state).isLoading &&
        pageType !== "CheckoutReviewAndSubmitPage" &&
        pageType !== "CheckoutShippingPage" &&
        pageType !== "OnePageCheckoutPage" &&
        pageType !== "CartPage" &&
        pageType !== "RfqRequestQuotePage";

    return {
        cart,
        shouldLoadCart,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currencyCode,
        isAuthenticated: getSession(state).isAuthenticated,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        isCartCheckoutDisabled: isCartCheckoutDisabled(cart),
        isSticky: state.xHeader.isSticky,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        // XNG-Changes
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    setNavDrawerIsOpen,
};

export interface CartLinkStyles {
    cartTotalStyles?: TypographyPresentationProps;
    cartQuantityStyles?: TypographyPresentationProps;
    titleIcon?: IconPresentationProps;
    titleClickable?: ClickablePresentationProps;
    miniCartElementStyle?: InjectableCss;
    drawer?: DrawerPresentationProps;
    // XNG-Changes
    cartItemsCount?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    cartTotalStyles: {
        size: fontConfig.fontSize.xxSmall,
        lineHeight: fontConfig.lineHeight.xSmall,
        color: "common.white",
        css: css`
            letter-spacing: 0.92px;
            margin-left: 6px;
            white-space: nowrap;
            &.checkoutPage-header {
                font-size: 15px;
                line-height: 23px;
            }
            ${xScreen("lt", "sm")} {
                &:not(.checkoutPage-header) {
                    display: none;
                }
            }
        `,
    },
    titleIcon: {
        css: css`
            ${xScreen("lte", "md")} {
                margin-bottom: 3px;
                &:not(.checkoutPage-header) {
                    &,
                    & > svg {
                        width: 28px;
                        height: 28px;
                    }
                }
            }
        `,
    },
    titleClickable: {
        css: css`
            position: relative;
            &:hover {
                span {
                    color: ${getColor("text.link")};
                    svg {
                        fill: ${getColor("text.link")};
                    }
                }
            }
        `,
    },
    miniCartElementStyle: {
        css: css`
            &.shade {
                span {
                    color: ${getColor("common.white")};
                    text-decoration: underline;
                    font-weight: 700;
                }
                svg {
                    fill: ${getColor("common.white")};
                }
                .title-clickable {
                    z-index: 20;
                }
            }
        `,
    },
    cartQuantityStyles: {
        size: 11,
        lineHeight: "13px",
        color: "common.white",
        css: css`
            letter-spacing: 0.51px;
        `,
    },
    drawer: {
        cssOverrides: {
            drawerContent: css`
                height: 100%;
                max-width: 440px;
            `,
            drawerBody: css`
                width: 440px;
                max-width: 440px;
            `,
        },
    },
    cartItemsCount: {
        size: 12,
        lineHeight: "14px",
        css: css`
            position: absolute;
            background: ${color("common.white")};
            color: ${color("text.main")};
            height: 15px;
            min-width: 15px;
            right: -2px;
            top: -4px;
            text-align: center;
            display: none;
            ${xScreen("lt", "sm")} {
                display: block;
            }
        `,
    },
};

interface CartTotalProps {
    cartTotal: string | undefined;
    isCheckoutPage?: boolean;
}

const CartTotal = ({ cartTotal, isCheckoutPage }: CartTotalProps) => {
    const showCartTotal = !isCheckoutPage;
    return (
        <>
            <Typography
                {...cartLinkStyles.cartTotalStyles}
                className={xClassNames.append(isCheckoutPage && "checkoutPage-header")}
            >
                {retainedTotalCountToDisplay
                    ? translate("{0} ITEMS", `${retainedTotalCountToDisplay}`)
                    : translate("CART")}
                {showCartTotal && cartTotal && ` | ${cartTotal}`}
            </Typography>
            {!isCheckoutPage && <Typography {...styles.cartItemsCount}>{retainedTotalCountToDisplay}</Typography>}
        </>
    );
};

interface OwnProps extends WidgetProps {
    fields: {};
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    orderSubTotalDisplay,
    loadCurrentCart,
    setNavDrawerIsOpen,
    navDrawerIsOpen,
    // XNG-Changes
    isCheckoutPage,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const [isOpen, setIsOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    React.useEffect(() => {
        const updateSmallScreen = () => {
            setIsSmallScreen(window.matchMedia("(max-width: 768px) and (hover: none)").matches);
        };
        updateSmallScreen();
        window.addEventListener("resize", updateSmallScreen, { passive: true });
        return () => {
            window.removeEventListener("resize", updateSmallScreen);
        };
    }, []);

    const handleCloseModal = () => {
        setTimeout(() => {
            setIsOpen(false);
        }, 300);
    };

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const { cartTotalStyles } = styles;
    const cartSubTotal = orderSubTotalDisplay;

    const cartElement = (isCheckoutPage?: boolean) => (
        <StyledWrapper
            css={css`
                position: relative;
                display: flex;
                align-items: center;
            `}
        >
            <Icon
                {...styles.titleIcon}
                size={16}
                src="Cart"
                color="common.white"
                className={xClassNames.append(isCheckoutPage && "checkoutPage-header")}
            />
            <CartTotal cartTotal={cartSubTotal} isCheckoutPage={isCheckoutPage} {...cartTotalStyles} />
        </StyledWrapper>
    );

    return (
        <>
            <Hidden below="md">
                <Clickable
                    {...styles.titleClickable}
                    data-test-selector="cartLink"
                    onClick={() => {
                        setTimeout(() => {
                            if (navDrawerIsOpen && !isOpen) {
                                setNavDrawerIsOpen({ navDrawerIsOpen: false });
                            }
                            setIsOpen(!isOpen);
                        }, 300);
                    }}
                >
                    {cartElement(isCheckoutPage)}
                </Clickable>
                {!isSmallScreen && (
                    <Drawer
                        draggable={false}
                        position="right"
                        {...styles.drawer}
                        isOpen={isOpen}
                        handleClose={handleCloseModal}
                        contentLabel="cart menu drawer"
                    >
                        <Suspense fallback={<LoadingOverlay loading={true} />}>
                            <XngMiniCart closeOnClick={handleCloseModal} />
                        </Suspense>
                    </Drawer>
                )}
            </Hidden>
            <Hidden above="sm">
                <Clickable {...styles.titleClickable} data-test-selector="cartLink" href={cartUrl}>
                    {cartElement(isCheckoutPage)}
                </Clickable>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLink),
    definition: {
        displayName: "Header Cart Link",
        icon: "cart-shopping",
        fieldDefinitions: [],
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
